import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function FaultFindingPage() {
  useEffect(() => {
    document.title = 'Fault Finding & Diagnostics Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert electrical fault finding in Watford. Advanced diagnostic equipment to identify circuit faults, tripping issues & power problems. NICEIC approved. Call 020 3198 1818.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Expert electrical fault finding in Watford. Advanced diagnostic equipment to identify circuit faults, tripping issues & power problems. NICEIC approved. Call 020 3198 1818.';
      document.head.appendChild(meta);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/repairs/fault-finding",
      "telephone": "02031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      },
      "serviceType": "Fault Finding & Diagnostics",
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    { emoji: '🔍', title: 'Circuit Testing', description: 'Advanced testing equipment to identify faulty circuits and wiring issues' },
    { emoji: '📊', title: 'Thermal Imaging', description: 'Infrared cameras to detect hot spots and overloaded circuits' },
    { emoji: '⚡', title: 'Power Quality Analysis', description: 'Testing voltage levels, frequency, and power factor issues' },
    { emoji: '🔌', title: 'Earth Fault Detection', description: 'Identifying dangerous earth leakage and grounding problems' },
    { emoji: '💡', title: 'Lighting Circuit Diagnosis', description: 'Finding faults in lighting circuits causing flickering or failures' },
    { emoji: '🛡️', title: 'RCD Testing', description: 'Testing residual current devices and identifying tripping causes' }
  ];

  const process = [
    { emoji: '📞', title: 'Initial Consultation', description: 'Discuss your electrical issues and symptoms over the phone' },
    { emoji: '🏠', title: 'Site Visit', description: 'Our electrician arrives with advanced diagnostic equipment' },
    { emoji: '🔍', title: 'Systematic Testing', description: 'Methodical testing of circuits, connections, and components' },
    { emoji: '📋', title: 'Fault Identification', description: 'Pinpoint the exact cause of the electrical problem' },
    { emoji: '💰', title: 'Repair Quote', description: 'Transparent pricing for the repair work required' },
    { emoji: '✅', title: 'Fix & Certify', description: 'Complete the repair and provide certification' }
  ];

  const benefits = [
    { emoji: '🎯', title: 'Accurate Diagnosis', description: 'Advanced equipment ensures we find the exact problem, not just symptoms' },
    { emoji: '💰', title: 'Cost-Effective', description: 'Avoid unnecessary work by identifying the real issue quickly' },
    { emoji: '⚡', title: 'Fast Resolution', description: 'Most faults diagnosed and repaired in a single visit' }
  ];

  const areas = [
    'Watford', 'Rickmansworth', 'Bushey', 'Abbots Langley', 'Croxley Green',
    'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Radlett', 'Borehamwood', 'St Albans'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white pt-32 pb-20">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/14949d098d425ce956eafcdd115d39a4.jpg')] bg-cover bg-center opacity-10"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Fault Finding & Diagnostics in Watford
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Expert electrical fault finding using advanced diagnostic equipment. We identify and fix the root cause, not just the symptoms.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-shield-check-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">NICEIC Approved</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-tools-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">Advanced Equipment</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-map-pin-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">Serving Watford</span>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:02031981818" className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line"></i>
                Get a Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Our Diagnostic Services
              </h2>
              <p className="text-xl text-gray-600">
                Comprehensive fault finding using the latest technology
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="w-16 h-16 flex items-center justify-center text-4xl mb-4">
                    {service.emoji}
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Our Fault Finding Process
              </h2>
              <p className="text-xl text-gray-600">
                Systematic approach to identifying electrical faults
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {process.map((step, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300 relative">
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center text-[#0A1628] font-bold text-xl">
                    {index + 1}
                  </div>
                  <div className="w-16 h-16 flex items-center justify-center text-4xl mb-4 mt-4">
                    {step.emoji}
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Why Choose Our Fault Finding Service
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center">
                  <div className="w-20 h-20 flex items-center justify-center text-5xl mx-auto mb-6">
                    {benefit.emoji}
                  </div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Beyond Electrical */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Why Choose Beyond Electrical
              </h2>
              <p className="text-xl text-gray-300">
                Expert electrical diagnostics in Watford
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-tools-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">Advanced Equipment</h3>
                <p className="text-gray-300">Latest diagnostic tools for accurate fault finding</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-shield-check-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">NICEIC Certified</h3>
                <p className="text-gray-300">All work certified and compliant with regulations</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-award-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">15+ Years Experience</h3>
                <p className="text-gray-300">Trusted by hundreds of Watford homeowners</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">500+</div>
                <div className="text-gray-300">Faults Diagnosed</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">95%</div>
                <div className="text-gray-300">First Visit Fix Rate</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">24/7</div>
                <div className="text-gray-300">Emergency Service</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">15+</div>
                <div className="text-gray-300">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-8">
              Areas We Cover
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Providing fault finding services throughout Watford and surrounding areas
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {areas.map((area, index) => (
                <span key={index} className="bg-gray-100 text-[#0A1628] px-6 py-3 rounded-full font-medium hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer">
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Need Expert Fault Finding in Watford?
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              Contact us today for professional electrical diagnostics. We'll find and fix the problem fast.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:02031981818" className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line"></i>
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
